import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import CategoryIcon from "../components/CategoryIcon";
import { toast } from "sonner";
import { CATEGORIES, CATEGORY_ORDER, PRIORITY_CITIES, OTHER_CITIES } from "../lib/categories";

const EMPTY = {
  category: "restaurant",
  name: "",
  description: "",
  subtype: "",
  city: "N'Djamena",
  address: "",
  phone: "",
  price_range: "$$",
  image_url: "",
  lat: 12.1348,
  lng: 15.0557,
  opening_hours: "",
  stars: "",
  monthly_rent: "",
  bedrooms: "",
  brand: "",
  model: "",
  year: "",
  mileage_km: "",
  sale_price: "",
  daily_rate: "",
  transmission: "",
};

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [places, setPlaces] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/places");
      setPlaces(data);
    } catch {
      setPlaces([]);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user]);

  if (loading || user === null) {
    return <div className="p-12 text-center text-brand-inkSoft">Chargement...</div>;
  }
  if (!user || user === false || user.role !== "admin") {
    return <Navigate to="/login" replace />;
  }

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...EMPTY,
      ...Object.fromEntries(Object.entries(p).map(([k, v]) => [k, v == null ? "" : v])),
    });
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Coerce numeric / optional fields
      const payload = {
        category: form.category,
        name: form.name,
        description: form.description || "",
        subtype: form.subtype,
        city: form.city,
        address: form.address,
        phone: form.phone || "",
        price_range: form.price_range || "$$",
        image_url: form.image_url || "",
        lat: parseFloat(form.lat),
        lng: parseFloat(form.lng),
        opening_hours: form.opening_hours || "",
      };
      // Category-specific
      if (form.category === "hotel" && form.stars !== "") payload.stars = parseInt(form.stars);
      if (form.category === "rental") {
        if (form.monthly_rent) payload.monthly_rent = form.monthly_rent;
        if (form.bedrooms !== "") payload.bedrooms = parseInt(form.bedrooms);
      }
      if (form.category === "car_sale") {
        if (form.brand) payload.brand = form.brand;
        if (form.model) payload.model = form.model;
        if (form.year !== "") payload.year = parseInt(form.year);
        if (form.mileage_km !== "") payload.mileage_km = parseInt(form.mileage_km);
        if (form.sale_price) payload.sale_price = form.sale_price;
        if (form.transmission) payload.transmission = form.transmission;
      }
      if (form.category === "car_rental") {
        if (form.brand) payload.brand = form.brand;
        if (form.model) payload.model = form.model;
        if (form.daily_rate) payload.daily_rate = form.daily_rate;
        if (form.transmission) payload.transmission = form.transmission;
      }

      if (editing) {
        await api.put(`/places/${editing.id}`, payload);
        toast.success("Annonce mise à jour");
      } else {
        await api.post("/places", payload);
        toast.success("Annonce créée");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer "${p.name}" ?`)) return;
    try {
      await api.delete(`/places/${p.id}`);
      toast.success("Annonce supprimée");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const filtered =
    filterCat === "all" ? places : places.filter((p) => p.category === filterCat);

  const currentCat = CATEGORIES[form.category] || CATEGORIES.restaurant;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="admin-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand-terracotta font-semibold mb-1">
            Administration
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-ink tracking-tight">
            Gestion des annonces
          </h1>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href="/ride/admin" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-brand-sandlight border border-brand-line text-brand-ink hover:border-brand-gold" data-testid="admin-link-ride">
              🚕 Kanama Ride
            </a>
            <a href="/courses/admin" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-brand-sandlight border border-brand-line text-brand-ink hover:border-brand-gold" data-testid="admin-link-grocery">
              🛒 Kanama Courses
            </a>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="h-11 rounded-full border-brand-line w-48" data-testid="admin-filter-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              {CATEGORY_ORDER.map((k) => (
                <SelectItem key={k} value={k}>{CATEGORIES[k].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={openCreate}
            className="rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white shadow-sm"
            data-testid="admin-create-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Nouvelle annonce
          </Button>
        </div>
      </div>

      <Card className="rounded-2xl border-brand-line bg-white overflow-hidden">
        <div className="divide-y divide-brand-line">
          {filtered.length === 0 ? (
            <div className="p-12 text-center text-brand-inkSoft">
              Aucune annonce. Cliquez sur "Nouvelle annonce" pour commencer.
            </div>
          ) : (
            filtered.map((p) => {
              const cat = CATEGORIES[p.category] || CATEGORIES.restaurant;
              return (
                <div
                  key={p.id}
                  className="flex items-center justify-between p-4 sm:p-5 hover:bg-brand-sandlight transition-colors gap-4"
                  data-testid={`admin-row-${p.id}`}
                >
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center text-white shrink-0"
                      style={{ backgroundColor: cat.color }}
                    >
                      <CategoryIcon name={cat.icon} className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span
                          className="text-[10px] uppercase tracking-wider font-bold"
                          style={{ color: cat.color }}
                        >
                          {cat.singular}
                        </span>
                      </div>
                      <div className="font-heading font-semibold text-brand-ink truncate">{p.name}</div>
                      <div className="text-xs text-brand-inkMuted truncate">
                        {p.subtype} · {p.city} · {p.price_range}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(p)}
                      className="rounded-full border-brand-line"
                      data-testid={`admin-edit-${p.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(p)}
                      className="rounded-full border-brand-line text-destructive hover:bg-red-50"
                      data-testid={`admin-delete-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editing ? "Modifier l'annonce" : "Nouvelle annonce"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4" data-testid="admin-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Catégorie *</Label>
                <Select value={form.category} onValueChange={updVal("category")}>
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="admin-input-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_ORDER.map((k) => (
                      <SelectItem key={k} value={k}>{CATEGORIES[k].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-2">
                <Label>Nom *</Label>
                <Input required value={form.name} onChange={upd("name")} className="mt-1.5 rounded-xl" data-testid="admin-input-name" />
              </div>

              <div>
                <Label>{currentCat.subtypeLabel} *</Label>
                <Input
                  required
                  value={form.subtype}
                  onChange={upd("subtype")}
                  placeholder={currentCat.subtypePlaceholder}
                  className="mt-1.5 rounded-xl"
                  data-testid="admin-input-subtype"
                />
              </div>

              <div>
                <Label>Gamme de prix</Label>
                <Select value={form.price_range} onValueChange={updVal("price_range")}>
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="admin-input-price">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="$">$ — Économique</SelectItem>
                    <SelectItem value="$$">$$ — Modéré</SelectItem>
                    <SelectItem value="$$$">$$$ — Élevé</SelectItem>
                    <SelectItem value="$$$$">$$$$ — Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Ville *</Label>
                <Select value={form.city} onValueChange={updVal("city")}>
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="admin-input-city">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-brand-terracotta font-bold">
                      Prioritaires
                    </div>
                    {PRIORITY_CITIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                    <div className="px-2 pt-2 pb-0.5 text-[10px] uppercase tracking-wider text-brand-inkMuted font-bold">
                      Autres
                    </div>
                    {OTHER_CITIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Téléphone</Label>
                <Input value={form.phone} onChange={upd("phone")} className="mt-1.5 rounded-xl" data-testid="admin-input-phone" />
              </div>

              <div className="sm:col-span-2">
                <Label>Adresse *</Label>
                <Input required value={form.address} onChange={upd("address")} className="mt-1.5 rounded-xl" data-testid="admin-input-address" />
              </div>

              <div>
                <Label>Latitude *</Label>
                <Input required type="number" step="any" value={form.lat} onChange={upd("lat")} className="mt-1.5 rounded-xl" data-testid="admin-input-lat" />
              </div>
              <div>
                <Label>Longitude *</Label>
                <Input required type="number" step="any" value={form.lng} onChange={upd("lng")} className="mt-1.5 rounded-xl" data-testid="admin-input-lng" />
              </div>

              <div className="sm:col-span-2">
                <Label>URL de l'image</Label>
                <Input value={form.image_url} onChange={upd("image_url")} placeholder="https://..." className="mt-1.5 rounded-xl" data-testid="admin-input-image" />
              </div>

              <div className="sm:col-span-2">
                <Label>Horaires</Label>
                <Input value={form.opening_hours} onChange={upd("opening_hours")} placeholder="ex: Lun-Sam : 8h - 19h" className="mt-1.5 rounded-xl" data-testid="admin-input-hours" />
              </div>

              {/* Category-specific fields */}
              {form.category === "hotel" && (
                <div>
                  <Label>Nombre d'étoiles</Label>
                  <Input type="number" min="1" max="5" value={form.stars} onChange={upd("stars")} className="mt-1.5 rounded-xl" data-testid="admin-input-stars" />
                </div>
              )}

              {form.category === "rental" && (
                <>
                  <div>
                    <Label>Loyer mensuel</Label>
                    <Input value={form.monthly_rent} onChange={upd("monthly_rent")} placeholder="ex: 200 000 FCFA" className="mt-1.5 rounded-xl" data-testid="admin-input-monthly-rent" />
                  </div>
                  <div>
                    <Label>Nombre de chambres</Label>
                    <Input type="number" min="1" value={form.bedrooms} onChange={upd("bedrooms")} className="mt-1.5 rounded-xl" data-testid="admin-input-bedrooms" />
                  </div>
                </>
              )}

              {(form.category === "car_sale" || form.category === "car_rental") && (
                <>
                  <div>
                    <Label>Marque</Label>
                    <Input value={form.brand} onChange={upd("brand")} placeholder="ex: Toyota" className="mt-1.5 rounded-xl" data-testid="admin-input-brand" />
                  </div>
                  <div>
                    <Label>Modèle</Label>
                    <Input value={form.model} onChange={upd("model")} placeholder="ex: Hilux" className="mt-1.5 rounded-xl" data-testid="admin-input-model" />
                  </div>
                  <div>
                    <Label>Transmission</Label>
                    <Select value={form.transmission || "none"} onValueChange={(v) => updVal("transmission")(v === "none" ? "" : v)}>
                      <SelectTrigger className="mt-1.5 rounded-xl" data-testid="admin-input-transmission">
                        <SelectValue placeholder="Choisir..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        <SelectItem value="Manuelle">Manuelle</SelectItem>
                        <SelectItem value="Automatique">Automatique</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {form.category === "car_sale" && (
                <>
                  <div>
                    <Label>Année</Label>
                    <Input type="number" min="1980" max="2030" value={form.year} onChange={upd("year")} className="mt-1.5 rounded-xl" data-testid="admin-input-year" />
                  </div>
                  <div>
                    <Label>Kilométrage (km)</Label>
                    <Input type="number" min="0" value={form.mileage_km} onChange={upd("mileage_km")} className="mt-1.5 rounded-xl" data-testid="admin-input-mileage" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Prix de vente</Label>
                    <Input value={form.sale_price} onChange={upd("sale_price")} placeholder="ex: 5 500 000 FCFA" className="mt-1.5 rounded-xl" data-testid="admin-input-sale-price" />
                  </div>
                </>
              )}

              {form.category === "car_rental" && (
                <div className="sm:col-span-2">
                  <Label>Tarif journalier</Label>
                  <Input value={form.daily_rate} onChange={upd("daily_rate")} placeholder="ex: 35 000 FCFA" className="mt-1.5 rounded-xl" data-testid="admin-input-daily-rate" />
                </div>
              )}

              <div className="sm:col-span-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={upd("description")} className="mt-1.5 rounded-xl min-h-[90px]" data-testid="admin-input-description" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full border-brand-line">
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white"
                data-testid="admin-save-button"
              >
                {saving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
